import React from "react";
import * as motion from "motion/react-client";
import {
  appearanceAnimation,
  zoomInAnimation
} from "../../../utils/animations";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { path } from "../../../utils/common/constant";

interface ProcessingPlanItem {
  number: string;
  title: string;
  description: string;
  price: string;
  isRecommended?: boolean;
}

const ProcessingPlanSection = () => {
  const { t } = useTranslation();

  const plansData: ProcessingPlanItem[] = [
    {
      number: "01",
      title: t("home.processing_plan.plan1.title"),
      price: "$10",
      description: t("home.processing_plan.plan1.description")
    },
    {
      number: "02",
      title: t("home.processing_plan.plan2.title"),
      price: "$20",
      description: t("home.processing_plan.plan2.description"),
      isRecommended: true
    },
    {
      number: "03",
      title: t("home.processing_plan.plan3.title"),
      price: "$30",
      description: t("home.processing_plan.plan3.description")
    }
  ];

  return (
    <div className="w-full flex justify-center items-center px-6 py-20">
      <div className="w-full max-w-[1290px] flex flex-col gap-28 justify-center items-center">
        {/* Title */}
        <motion.div
          {...appearanceAnimation("bottom")}
          className="flex flex-col justify-center items-center gap-6"
        >
          <p className="text-[#074799] text-sm font-semibold">
            {t("home.processing_plan")}
          </p>
          <p className="text-[#074799] md:text-5xl text-3xl font-bold">
            {t("home.processing_plan.title")}
          </p>
        </motion.div>

        <div className="w-full grid lg:grid-cols-3 grid-cols-1 gap-8">
          {plansData.map((plan, index) => (
            <div
              key={index}
              className="w-full h-full flex justify-center items-center"
            >
              <ProcessingPlanCard t={t} plan={plan} index={index} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProcessingPlanSection;

interface ProcessingPlanCardProps {
  t: any;
  plan: ProcessingPlanItem;
  index: number;
}

const ProcessingPlanCard: React.FC<ProcessingPlanCardProps> = ({
  t,
  plan,
  index
}) => {
  const navigate = useNavigate();

  return (
    <motion.div
      {...zoomInAnimation(index)}
      className="p-8 border flex flex-col justify-center gap-6 rounded-2xl w-full hover:-translate-y-3 cursor-pointer hover:shadow-xl duration-300"
      style={{
        height: plan.number === "02" ? "110%" : "auto",
        backgroundColor: plan.number === "02" ? "#074799" : "white",
        color: plan.number === "02" ? "white" : "#074799"
      }}
      onClick={() =>
        navigate("../" + path.VISA_APPLICATION, {
          state: {
            processingTime:
              plan.number === "01"
                ? "Standard"
                : plan.number === "02"
                ? "Express"
                : "Urgent",
            typeVisa: "OneMonthSingle"
          }
        })
      }
    >
      <p
        className={`text-[50px] font-bold ${
          plan.number === "02"
            ? "text-white"
            : "text-transparent custom-text-stroke"
        }`}
      >
        {plan.number}
      </p>

      <p className="text-[22px] font-bold">
        {plan.title}
        <span className="text-base ml-2">
          {plan.isRecommended && `(${t("home.processing_plan.recommended")})`}
        </span>
      </p>

      <p className="text-5xl font-bold">{plan.price}</p>

      <p className="text-justify">{plan.description}</p>
    </motion.div>
  );
};
